import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button, Card, Col, Container, Row, Spinner, Table } from "react-bootstrap";
import { FaArrowLeft, FaEye } from "react-icons/fa";
import PageLayout from "../../../layouts/PageLayout";

const PAGE_TITLES = {
  sales: "All Sales",
  appointments: "All Appointments",
  billed: "Total Bill",
  paid: "Money Received",
  unpaid: "Money Not Paid Yet"
};

const formatAmount = (value) =>
  parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

const formatDate = (value) => {
  if (!value) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }).format(new Date(value));
};

const sumField = (rows, field) =>
  rows.reduce((total, row) => total + (parseFloat(row[field]) || 0), 0);

const EyeButton = ({ onClick, title }) => (
  <Button variant="outline-primary" size="sm" onClick={onClick} title={title} aria-label={title}>
    <FaEye />
  </Button>
);

export default function DashboardDetailsPage() {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchParams.get("start_date")) params.append("start_date", searchParams.get("start_date"));
        if (searchParams.get("end_date")) params.append("end_date", searchParams.get("end_date"));

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/details?${params.toString()}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
        );

        if (response.data.success) {
          setData(response.data);
        }
      } catch (error) {
        console.error("Error fetching dashboard details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [searchParams]);

  const sales = data?.sales || [];
  const appointments = data?.appointments || [];
  const payments = data?.payments || [];
  const outstanding = (rows) => rows.filter((row) => row.unpaid > 0.005);

  const sections = (() => {
    switch (type) {
      case "sales":
        return [{ title: "Sales", kind: "billing", rows: sales }];
      case "appointments":
        return [{ title: "Appointments", kind: "billing", rows: appointments }];
      case "billed":
        return [
          { title: "Sales", kind: "billing", rows: sales },
          { title: "Appointments", kind: "billing", rows: appointments }
        ];
      case "paid":
        return [{ title: "Payments Received", kind: "payments", rows: payments }];
      case "unpaid":
        return [
          { title: "Sales Not Paid", kind: "billing", rows: outstanding(sales) },
          { title: "Appointments Not Paid", kind: "billing", rows: outstanding(appointments) }
        ];
      default:
        return [];
    }
  })();

  const billingRows = sections
    .filter((section) => section.kind === "billing")
    .flatMap((section) => section.rows);

  const openSale = (row) => navigate(`/sale-edit/${row.id}`);
  const openAppointment = (row) => navigate(`/appointments/${row.id}`);

  const renderSummary = () => {
    if (type === "paid") {
      return (
        <SummaryCards
          items={[{ label: "Total Received", value: sumField(payments, "amount"), tone: "text-success" }]}
        />
      );
    }

    if (type === "sales" || type === "appointments" || type === "billed" || type === "unpaid") {
      return (
        <SummaryCards
          items={[
            { label: "Total Bill", value: sumField(billingRows, "total"), tone: "text-dark" },
            { label: "Received", value: sumField(billingRows, "paid"), tone: "text-success" },
            { label: "Not Paid", value: sumField(billingRows, "unpaid"), tone: "text-danger" }
          ]}
        />
      );
    }

    return null;
  };

  const renderBillingTable = (section) => (
    <div className="mb-4" key={section.title}>
      <h5 className="fw-bold mb-2">
        {section.title} <span className="text-muted small">({section.rows.length})</span>
      </h5>
      {section.rows.length === 0 ? (
        <div className="text-muted py-2">No records found</div>
      ) : (
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th>No.</th>
              <th>Date</th>
              <th>Customer</th>
              <th className="text-end">Total</th>
              <th className="text-end">Received</th>
              <th className="text-end">Not Paid</th>
              <th className="text-center">View</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row) => {
              const isSale = section.title === "Sales" || section.title === "Sales Not Paid";

              return (
                <tr key={`${section.title}-${row.id}`}>
                  <td><strong className="text-primary">{row.number}</strong></td>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.customer || "-"}</td>
                  <td className="text-end">{formatAmount(row.total)}</td>
                  <td className="text-end text-success">{formatAmount(row.paid)}</td>
                  <td className={`text-end fw-bold ${row.unpaid > 0.005 ? "text-danger" : ""}`}>
                    {formatAmount(row.unpaid)}
                  </td>
                  <td className="text-center">
                    <EyeButton
                      title={isSale ? "View sale" : "View appointment"}
                      onClick={() => (isSale ? openSale(row) : openAppointment(row))}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="table-active">
            <tr>
              <th colSpan="3" className="text-end">Total</th>
              <th className="text-end">{formatAmount(sumField(section.rows, "total"))}</th>
              <th className="text-end">{formatAmount(sumField(section.rows, "paid"))}</th>
              <th className="text-end">{formatAmount(sumField(section.rows, "unpaid"))}</th>
              <th></th>
            </tr>
          </tfoot>
        </Table>
      )}
    </div>
  );

  const renderPaymentsTable = (section) => (
    <div className="mb-4" key={section.title}>
      <h5 className="fw-bold mb-2">
        {section.title} <span className="text-muted small">({section.rows.length})</span>
      </h5>
      {section.rows.length === 0 ? (
        <div className="text-muted py-2">No records found</div>
      ) : (
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Date</th>
              <th>For</th>
              <th>No.</th>
              <th>Customer</th>
              <th>Paid With</th>
              <th className="text-end">Amount</th>
              <th className="text-center">View</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row, idx) => (
              <tr key={`${row.type}-${row.id}-${idx}`}>
                <td>{formatDate(row.date)}</td>
                <td>{row.type === "Sale" ? "Sale" : "Appointment"}</td>
                <td><strong className="text-primary">{row.reference}</strong></td>
                <td>{row.customer || "-"}</td>
                <td>{row.method || "-"}</td>
                <td className={`text-end fw-bold ${row.amount < 0 ? "text-danger" : "text-success"}`}>
                  {formatAmount(row.amount)}
                </td>
                <td className="text-center">
                  <EyeButton
                    title={row.type === "Sale" ? "View sale" : "View appointment"}
                    onClick={() => (row.type === "Sale" ? openSale(row) : openAppointment(row))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="table-active">
            <tr>
              <th colSpan="5" className="text-end">Total Received</th>
              <th className="text-end">{formatAmount(sumField(section.rows, "amount"))}</th>
              <th></th>
            </tr>
          </tfoot>
        </Table>
      )}
    </div>
  );

  return (
    <PageLayout>
      <Container fluid className="py-3">
        <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-1" /> Back
          </Button>
          <h3 className="mb-0 fw-bold">{PAGE_TITLES[type] || "Details"}</h3>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading...</p>
          </div>
        ) : (
          <>
            {renderSummary()}

            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3 p-md-4">
                {sections.map((section) =>
                  section.kind === "payments"
                    ? renderPaymentsTable(section)
                    : renderBillingTable(section)
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </PageLayout>
  );
}

function SummaryCards({ items }) {
  return (
    <Row className="g-3 mb-4">
      {items.map((item) => (
        <Col xs={12} md={4} key={item.label}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-3">
              <div className="text-muted small">{item.label}</div>
              <div className={`h3 fw-bold mb-0 ${item.tone}`}>{formatAmount(item.value)}</div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
}
